import { Calendar, Clock, User, Filter, Search, ChevronLeft, ChevronRight, Radio } from 'lucide-react';
import { useState } from 'react';

interface ScheduledShow {
  id: number;
  date: string;
  time: string;
  title: string;
  host: string;
  type: string;
  duration: string;
  isLive: boolean;
  description: string;
}

const initialSchedule: ScheduledShow[] = [
  { id: 1, date: '2025-12-06', time: '15:00', title: 'Indie Rock Hour', host: 'Alex Turner', type: 'Music', duration: '60 min', isLive: false, description: 'The best indie rock tracks from around the world' },
  { id: 2, date: '2025-12-06', time: '16:00', title: 'Tech Talk Daily', host: 'Marques B.', type: 'Talk', duration: '60 min', isLive: false, description: 'Latest technology news and gadget reviews' },
  { id: 3, date: '2025-12-06', time: '17:00', title: 'Evening News', host: 'News Team', type: 'News', duration: '30 min', isLive: true, description: 'Your daily news roundup' },
  { id: 4, date: '2025-12-06', time: '18:00', title: 'Jazz & Blues', host: 'Norah J.', type: 'Music', duration: '120 min', isLive: false, description: 'Smooth jazz and blues classics' },
  { id: 5, date: '2025-12-06', time: '20:00', title: 'Weekend Warmup', host: 'DJ Mike', type: 'Music', duration: '180 min', isLive: true, description: 'Party music to kickstart your weekend' },
  { id: 6, date: '2025-12-07', time: '06:00', title: 'Morning Show', host: 'Sarah Johnson', type: 'Talk', duration: '240 min', isLive: true, description: 'Start your day with news, music, and interviews' },
  { id: 7, date: '2025-12-07', time: '10:00', title: 'Classical Morning', host: 'Elena Martinez', type: 'Music', duration: '120 min', isLive: false, description: 'Classical masterpieces for a peaceful morning' },
  { id: 8, date: '2025-12-07', time: '12:00', title: 'Lunch Hour Mix', host: 'Chris Lee', type: 'Music', duration: '60 min', isLive: false, description: 'Upbeat tracks for your lunch break' },
  { id: 9, date: '2025-12-07', time: '14:00', title: 'Sports Talk', host: 'Tom Brady', type: 'Sports', duration: '120 min', isLive: true, description: 'All the latest sports news and analysis' },
  { id: 10, date: '2025-12-07', time: '16:00', title: 'Pop Culture Hour', host: 'Emma Stone', type: 'Talk', duration: '60 min', isLive: false, description: 'Movies, music, and celebrity news' },
  { id: 11, date: '2025-12-08', time: '08:00', title: 'Sunday Spiritual', host: 'Pastor John', type: 'Religious', duration: '90 min', isLive: true, description: 'Spiritual guidance and gospel music' },
  { id: 12, date: '2025-12-08', time: '11:00', title: 'World Music', host: 'Diego Santos', type: 'Music', duration: '120 min', isLive: false, description: 'Music from around the globe' },
];

const showTypes = ['All', 'Music', 'Talk', 'News', 'Sports', 'Religious'];

export default function UpcomingSchedule() {
  const [schedule, _setSchedule] = useState<ScheduledShow[]>(initialSchedule);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterDate, setFilterDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Filtering Logic
  const filteredSchedule = schedule.filter(show => {
    const matchesSearch = 
      show.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      show.host.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'All' || show.type === filterType;
    const matchesDate = !filterDate || show.date === filterDate;
    return matchesSearch && matchesType && matchesDate;
  });

  // Pagination Logic
  const totalPages = Math.ceil(filteredSchedule.length / itemsPerPage);
  const paginatedSchedule = filteredSchedule.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Group by date for better organization
  const groupedByDate = paginatedSchedule.reduce((acc, show) => {
    if (!acc[show.date]) {
      acc[show.date] = [];
    }
    acc[show.date].push(show);
    return acc;
  }, {} as Record<string, ScheduledShow[]>);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Upcoming Schedule</h1>
          <p className="text-slate-400 mt-1">View all scheduled shows and programs.</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium">
          <Radio className="w-4 h-4" />
          {filteredSchedule.length} Shows Scheduled
        </div>
      </div>

      {/* Controls Toolbar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-[#0B1623] border border-white/10 p-4 rounded-2xl">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by show title or host..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="flex-1 bg-black/20 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500 transition-all"
            >
              {showTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="flex-1 bg-black/20 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Schedule View */}
      <div className="space-y-6">
        {Object.entries(groupedByDate).map(([date, shows]) => (
          <div key={date} className="bg-[#0B1623] border border-white/10 rounded-2xl overflow-hidden">
            {/* Date Header */}
            <div className="bg-linear-to-r from-cyan-600/20 to-blue-600/20 border-b border-white/10 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-cyan-400" />
                {formatDate(date)}
              </h3>
            </div>

            {/* Shows for this date */}
            <div className="divide-y divide-white/5">
              {shows.map((show) => (
                <div 
                  key={show.id} 
                  className="p-6 hover:bg-white/2 transition-colors group"
                >
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Time Column */}
                    <div className="shrink-0 w-32">
                      <div className="flex items-center gap-2 text-cyan-400 mb-2">
                        <Clock className="w-4 h-4" />
                        <span className="text-xl font-bold font-mono">{show.time}</span>
                      </div>
                      <div className="text-xs text-slate-500">{show.duration}</div>
                    </div>

                    {/* Show Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h4 className="text-lg font-bold text-white group-hover:text-cyan-300 transition-colors">
                          {show.title}
                        </h4>
                        {show.isLive && (
                          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-xs font-medium animate-pulse">
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                            LIVE
                          </span>
                        )}
                        <span className="px-2.5 py-1 rounded-md bg-purple-500/10 text-purple-400 border border-purple-500/20 text-xs font-medium">
                          {show.type}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
                        <User className="w-4 h-4" />
                        <span>Host: {show.host}</span>
                      </div>

                      <p className="text-sm text-slate-400 leading-relaxed">
                        {show.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {filteredSchedule.length === 0 && (
          <div className="bg-[#0B1623] border border-white/10 rounded-2xl p-12 text-center">
            <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">No scheduled shows found matching your filters.</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4">
          <button 
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-white/10 text-slate-400 hover:bg-white/5 disabled:opacity-50 disabled:hover:bg-transparent transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-slate-400">
            Page {currentPage} of {totalPages} ({filteredSchedule.length} shows)
          </span>
          <button 
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-white/10 text-slate-400 hover:bg-white/5 disabled:opacity-50 disabled:hover:bg-transparent transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
